/* eslint-disable max-lines */
import classNames from 'classnames';

import {injectIntl} from 'react-intl';

import {Editor as TextEditor} from 'packages/editor';

import * as Utils from 'utils/utils';

import {SendButton} from '@time-webkit/all/molecules/send-button';

import FilePreview from 'components/file_preview';
import FileUpload from 'components/file_upload';
import MsgTyping from 'components/msg_typing';
import {CreatePostMoreButton} from 'components/textbox_more_button';

import {MessageSubmitError} from 'components/message_submit_error';

import PostQuote from 'components/post_view/post_quote';
import {TextboxError} from 'components/textbox/textbox_error';

import {BadConnection} from '../bad_connection';

import {useEditor} from '../hooks/useEditor';

import {EmojiPicker} from '../emoji_picker';

import {ChannelType} from 'features/sidebar';

import {type EditorProps} from './types';

import {getPostType} from './helpers';

import styles from './styles.module.css';

const editorFeatures = {
    mention: {
        isActive: true,
    },
    codeBlock: {
        isActive: true,
    },
};

export const Editor = (props: EditorProps) => {
    const editor = useEditor(props);

    const postType = getPostType(editor.props.isThread, editor.props.isThreadView);

    const preview =
        (editor.state.fileInfos.length > 0 || editor.state.uploadsInProgress.length > 0) ? (
            <FilePreview
                fileInfos={editor.state.fileInfos}
                onRemove={editor.actions.removePreview}
                uploadsInProgress={editor.state.uploadsInProgress}
                uploadsProgressPercent={editor.state.uploadsProgressPercent}
            />
        ) : null;

    const renderMoreButton = (node: React.ReactNode, onToggle: (open: boolean) => void) => {
        const handleToggle = (open: boolean) => {
            onToggle(open);
            if (!open) {
                editor?.handlers.handleFocusReturn();
            }
        };

        return (
            <CreatePostMoreButton
                pluginItems={editor.pluginItems}
                onToggle={handleToggle}
            >
                {node}
            </CreatePostMoreButton>
        );
    };

    return (
        <form
            id={editor.props.isThread ? 'create_comment' : 'create_post'}
            ref={editor.refs.topDiv}
            className={classNames(
                editor.props.fullWidthTextBox ? '' : 'center',
                styles.form,
            )}
            onSubmit={editor.handlers.handleSubmit}
            data-qa='form_create_post'
        >
            <MsgTyping
                channelId={editor.props.currentChannelId}
                postId={props.rootId ?? ''}
            />
            <div
                className={classNames('post-create', styles.wrapper, editor.classNames.attachmentsDisabledClass, {'post-create--within-thread': props.isThread})}
            >
                {editor.state.quote ? (
                    <div className='post-create__quote' data-testid='PostQuote'>
                        <PostQuote
                            post={editor.state.quote}
                            teamName={editor.props.teamName}
                            channelType={editor.props.currentChannelType}
                            isPreview={true}
                            onClose={editor.handlers.handlePostQuoteClose}
                            isEdit={editor.props.isEditing}
                        />
                    </div>
                ) : null}
                <div className='post-create-body' style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
                    <div
                        role='application'
                        id='centerChannelFooter'
                        aria-label={Utils.localizeMessage(
                            'accessibility.sections.centerFooter',
                            'message input complimentary region',
                        )}
                        tabIndex={-1}
                        className={classNames('post-body__cell a11y__region', styles.editorContainer)}
                        data-a11y-sort-order='2'
                    >
                        {editor.props.badConnection && <BadConnection />}
                        {
                            <TextEditor
                                useCustomGroupMentions={props.useCustomGroupMentions}
                                useLDAPGroupMentions={props.useLDAPGroupMentions}
                                onContentChange={editor.handlers.handleChange}
                                channelId={editor.props.currentChannelId}
                                teamId={editor.props.currentTeamId}
                                onEscPress={editor.handlers.handleEscPress}
                                onEnterPress={editor.handlers.postMsgKeyPress}
                                onArrowUpPress={editor.handlers.editLastPostKeyPress}
                                onReactLastPost={editor.handlers.handleReactLastPost}
                                onReplyLastPost={editor.handlers.handleReplyLastPost}
                                draftMessage={editor.props.draft.message}
                                disabled={editor.props.badConnection}
                                onCaretPositionChange={editor.handlers.handleCaretPositionChange}
                                onHandleKeyDown={editor.handlers.handleKeyDown}
                                ctrlSend={editor.props.ctrlSend}
                                codeBlockOnCtrlEnter={editor.props.codeBlockOnCtrlEnter}
                                maxCharacters={editor.props.maxPostSize}
                                charactersCount={editor.state.message?.length ?? 0}
                                placeholder={editor.placeholder}
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                features={editorFeatures}
                                predefinedMentions={editor.predefinedMentions}
                                setEditor={editor.handlers.setEditor}
                                fileAttachmentSlot={preview}
                                withinThread={editor.props.isThread}
                                withinDirect={editor.props.currentChannelType === ChannelType.DM}
                                rootId={editor.props.rootId}
                                emojiPicker={
                                    editor.props.enableEmojiPicker &&
                                    !editor.props.shouldShowPreview && (
                                        <EmojiPicker editor={editor}/>
                                    )}
                                fileUploader={!editor.props.shouldShowPreview && (
                                    <FileUpload
                                        ref={editor.refs.fileUploadRef as any}
                                        fileCount={
                                            editor.state.fileInfos.length + editor.state.uploadsInProgress.length
                                        }
                                        getTarget={() => editor.editorReference?.view.dom}
                                        onFileUploadChange={editor.handlers.handleFileUploadChange}
                                        onUploadStart={editor.handlers.handleUploadStart}
                                        onFileUpload={editor.handlers.handleFileUploadComplete}
                                        onUploadClientError={editor.handlers.handleUploadClientError}
                                        onUploadServerError={editor.handlers.handleUploadServerError}
                                        onUploadProgress={editor.handlers.handleUploadProgress}
                                        onReturnFocus={editor.handlers.handleFocusReturn}
                                        postType={postType}
                                        channelId={editor.props.currentChannelId}
                                    />
                                )}
                                sendMenuSlot={
                                    <span
                                        ref={editor.refs.createPostControlsRef}
                                        className={classNames('post-body__actions post-body__actions--advancededitor', styles.actions)}
                                    >
                                        <SendButton
                                            isDisabled={
                                                editor.props.isEditing ? editor.actions.isEditSaveDisabled() : !editor.actions.shouldEnableSendButton()
                                            }
                                            onPress={editor.props.isEditing ? editor.handlers.handleEdit : editor.handlers.handleSubmit}
                                            isEdit={editor.props.isEditing}
                                            loading={editor.state.editing || editor.state.submitting}
                                            data-qa='button_send_message'
                                            isRightButtonEnabled={editor.pluginItems.length > 0}
                                            renderWithMenuWrapper={editor.props.isEditing ? undefined : renderMoreButton}
                                        />
                                    </span>
                                }
                                dataTestId={props.dataTestId}
                                setServerError={editor.actions.setServerError}
                            />
                        }
                    </div>
                </div>
                <div
                    id='postCreateFooter'
                    role='form'
                    className={styles.footer}
                >
                    {editor.state.postError && (
                        <TextboxError error={editor.state.postError}/>
                    )}
                    {editor.state.serverError && (
                        <MessageSubmitError
                            error={editor.state.serverError}
                            submittedMessage={editor.state.serverError.submittedMessage}
                            handleSubmit={editor.handlers.handleSubmit}
                        />
                    )}
                </div>
            </div>
        </form>
    );
};

export default injectIntl(Editor, {forwardRef: true});
/* eslint-enable react/no-string-refs */
